/**
 * Custom adjustments (owner's decision, 2026-11-03): Profi includes 2 smaller custom adjustments a calendar year
 * (at most 4 hours of work each), Prémium 4 with priority, Alap none (it can be ordered by individual offer).
 * Examples: own work order layout, an extra field, a custom export, a simple CSV link. Bigger work is an
 * individual offer. An adjustment is built as a setting or a switchable module for that workshop, never as a
 * separate program. The operator records them (public.custom_requests, 20261216000000_custom_requests.sql); a
 * database test keeps these numbers equal to public.custom_adjustments_included.
 */
import type { Plan } from "./features";

/** Included smaller adjustments a calendar year, by plan. */
export const CUSTOM_ADJUSTMENTS_PER_YEAR: Record<Plan, number> = { basic: 0, medium: 2, premium: 4 };
/** At most this many hours of work for one included adjustment. */
export const CUSTOM_ADJUSTMENT_MAX_HOURS = 4;

export const CUSTOM_REQUEST_STATUSES = ["open", "done", "cancelled"] as const;
export type CustomRequestStatus = (typeof CUSTOM_REQUEST_STATUSES)[number];

export interface CustomRequest {
  id: string;
  year: number;
  title: string;
  hours: number;
  /** Counts as one of the adjustments included in the package; false: individual offer. */
  included: boolean;
  status: CustomRequestStatus;
  createdAt: string;
}

/** Included requests of the year that are not cancelled. */
export function customAdjustmentsUsed(requests: readonly Pick<CustomRequest, "year" | "included" | "status">[], year: number): number {
  return requests.filter((r) => r.year === year && r.included && r.status !== "cancelled").length;
}

/** Why the operator cannot record this request (same order as public.op_add_custom_request), or null. */
export function customRequestRefusal(
  input: { title: string; hours: number | null; included: boolean },
  used: number,
  plan: Plan,
): "REQUIRED_MISSING" | "NOT_ALLOWED" | "CUSTOM_LIMIT" | null {
  if (input.title.trim().length < 3 || input.hours === null || !Number.isFinite(input.hours)) return "REQUIRED_MISSING";
  if (input.included && used >= CUSTOM_ADJUSTMENTS_PER_YEAR[plan]) return "CUSTOM_LIMIT";
  if (input.hours <= 0 || input.hours > 1000 || (input.included && input.hours > CUSTOM_ADJUSTMENT_MAX_HOURS) || input.title.trim().length > 200) return "NOT_ALLOWED";
  return null;
}
